import * as t from 'io-ts';

import {makeApiSerializableType, RChannelIdRef, RExternalChannelTypeIdRef, RInboxIdRef} from './apiTypes';
import {parseChannelBehaviors, RChannelBehaviors} from './channelBehaviorsModel';
import {ChannelTypesEnum} from './channelCommonTypes';
import {parseChannelError, RChannelError} from './channelErrorModel';
import {
  parseChannelPreferencesBase,
  parseChannelPreferencesCustom,
  parseChannelPreferencesFrontChat,
  parseChannelPreferencesFrontMail,
  parseChannelPreferencesGmail,
  parseChannelPreferencesOffice365,
  parseChannelPreferencesSmooch,
  parseChannelPreferencesSmtp,
  parseChannelPreferencesTwitter,
  RChannelPreferences,
  RChannelPreferencesCustom,
  RChannelPreferencesFrontChat,
  RChannelPreferencesFrontMail,
  RChannelPreferencesGmail,
  RChannelPreferencesOffice365,
  RChannelPreferencesSmooch,
  RChannelPreferencesSmtp,
  RChannelPreferencesTwitter,
  serializeChannelPreferences,
} from './channelPreferencesModel';
import {normalizeHandle, RContactSource} from './contactModel';
import {parseMessageType, RMessageType} from './messageModel';

/*
 * Runtime type.
 */

const RChannelBase = t.type({
  id: t.Integer,
  namespace: t.string,
  inboxId: RInboxIdRef,
  displayName: t.string,
  name: t.string,
  address: t.string,
  sendAddress: t.string,
  typeName: t.string,
  messageType: RMessageType,
  contactSource: RContactSource,
  behaviors: RChannelBehaviors,
  preferences: RChannelPreferences,
  externalChannelTypeId: t.union([t.undefined, RExternalChannelTypeIdRef]),
  updatedAt: t.Integer,
  url: t.string,
  error: t.union([RChannelError, t.undefined]),
});

const RChannelFrontChat = t.intersection([
  RChannelBase,
  t.type({
    type: t.literal(ChannelTypesEnum.FRONT_CHAT),
    preferences: RChannelPreferencesFrontChat,
  }),
]);

const RChannelCustom = t.intersection([
  RChannelBase,
  t.type({
    type: t.literal(ChannelTypesEnum.CUSTOM),
    preferences: RChannelPreferencesCustom,
    apiUrl: t.string,
  }),
]);
const RChannelFacebook = t.intersection([
  RChannelBase,
  t.type({
    type: t.literal(ChannelTypesEnum.FACEBOOK),
    preferences: RChannelPreferences,
  }),
]);
const RChannelGooglePlay = t.intersection([
  RChannelBase,
  t.type({
    type: t.literal(ChannelTypesEnum.GOOGLE_PLAY),
    preferences: RChannelPreferences,
  }),
]);
const RChannelIntercom = t.intersection([
  RChannelBase,
  t.type({
    type: t.literal(ChannelTypesEnum.INTERCOM),
    preferences: RChannelPreferences,
  }),
]);
const RChannelForm = t.intersection([
  RChannelBase,
  t.type({
    type: t.literal(ChannelTypesEnum.FORM),
    preferences: RChannelPreferences,
  }),
]);
const RChannelFrontMail = t.intersection([
  RChannelBase,
  t.type({
    type: t.literal(ChannelTypesEnum.FRONT_MAIL),
    preferences: RChannelPreferencesFrontMail,
  }),
]);
const RChannelOffice365 = t.intersection([
  RChannelBase,
  t.type({
    type: t.literal(ChannelTypesEnum.OFFICE365),
    preferences: RChannelPreferencesOffice365,
  }),
]);
const RChannelGmail = t.intersection([
  RChannelBase,
  t.type({
    type: t.literal(ChannelTypesEnum.GMAIL),
    preferences: RChannelPreferencesGmail,
  }),
]);
const RChannelSmtp = t.intersection([
  RChannelBase,
  t.type({
    type: t.literal(ChannelTypesEnum.SMTP),
    preferences: RChannelPreferencesSmtp,
    sendAsDomain: t.union([t.undefined, t.string]),
  }),
]);
const RChannelSmooch = t.intersection([
  RChannelBase,
  t.type({
    type: t.literal(ChannelTypesEnum.SMOOCH),
    preferences: RChannelPreferencesSmooch,
  }),
]);
const RChannelTalkdesk = t.intersection([
  RChannelBase,
  t.type({
    type: t.literal(ChannelTypesEnum.TALKDESK),
    preferences: RChannelPreferences,
  }),
]);
const RChannelTruly = t.intersection([
  RChannelBase,
  t.type({
    type: t.literal(ChannelTypesEnum.TRULY),
    preferences: RChannelPreferences,
  }),
]);
const RChannelTwilio = t.intersection([
  RChannelBase,
  t.type({
    type: t.literal(ChannelTypesEnum.TWILIO),
    preferences: RChannelPreferences,
  }),
]);
const RChannelTwilioWhatsapp = t.intersection([
  RChannelBase,
  t.type({
    type: t.literal(ChannelTypesEnum.TWILIO_WHATSAPP),
    preferences: RChannelPreferences,
  }),
]);
const RChannelTwitter = t.intersection([
  RChannelBase,
  t.type({
    type: t.literal(ChannelTypesEnum.TWITTER),
    preferences: RChannelPreferencesTwitter,
  }),
]);
const RChannelTwitterDm = t.intersection([
  RChannelBase,
  t.type({
    type: t.literal(ChannelTypesEnum.TWITTER_DM),
    preferences: RChannelPreferences,
  }),
]);
const RChannelYaloWhatsapp = t.intersection([
  RChannelBase,
  t.type({
    type: t.literal(ChannelTypesEnum.YALO_WHATSAPP),
    preferences: RChannelPreferences,
  }),
]);

const RChannelDirty = t.union(
  [
    RChannelCustom,
    RChannelFacebook,
    RChannelForm,
    RChannelFrontChat,
    RChannelGmail,
    RChannelGooglePlay,
    RChannelIntercom,
    RChannelOffice365,
    RChannelSmooch,
    RChannelSmtp,
    RChannelTalkdesk,
    RChannelTruly,
    RChannelTwilio,
    RChannelTwilioWhatsapp,
    RChannelTwitterDm,
    RChannelTwitter,
    RChannelYaloWhatsapp,
    RChannelFrontMail,
  ],
  'channel',
);

/*
 * Type.
 */

export interface ChannelCustomModel extends t.TypeOf<typeof RChannelCustom> {}
export interface ChannelFacebookModel extends t.TypeOf<typeof RChannelFacebook> {}
export interface ChannelFormModel extends t.TypeOf<typeof RChannelForm> {}
export interface ChannelFrontChatModel extends t.TypeOf<typeof RChannelFrontChat> {}
export interface ChannelGmailModel extends t.TypeOf<typeof RChannelGmail> {}
export interface ChannelGooglePlayModel extends t.TypeOf<typeof RChannelGooglePlay> {}
export interface ChannelIntercomModel extends t.TypeOf<typeof RChannelIntercom> {}
export interface ChannelOffice365Model extends t.TypeOf<typeof RChannelOffice365> {}
export interface ChannelSmoochModel extends t.TypeOf<typeof RChannelSmooch> {}
export interface ChannelSmtpModel extends t.TypeOf<typeof RChannelSmtp> {}
export interface ChannelTalkdeskModel extends t.TypeOf<typeof RChannelTalkdesk> {}
export interface ChannelTrulyModel extends t.TypeOf<typeof RChannelTruly> {}
export interface ChannelTwilioModel extends t.TypeOf<typeof RChannelTwilio> {}
export interface ChannelTwilioWhatsappModel extends t.TypeOf<typeof RChannelTwilioWhatsapp> {}
export interface ChannelTwitterDmModel extends t.TypeOf<typeof RChannelTwitterDm> {}
export interface ChannelTwitterModel extends t.TypeOf<typeof RChannelTwitter> {}
export interface ChannelYaloWhatsappModel extends t.TypeOf<typeof RChannelYaloWhatsapp> {}
export interface ChannelFrontMailModel extends t.TypeOf<typeof RChannelFrontMail> {}

export type ChannelEmailModel =
  | ChannelGmailModel
  | ChannelOffice365Model
  | ChannelSmtpModel
  | ChannelFrontMailModel;

export type ChannelModel =
  | ChannelFrontMailModel
  | ChannelCustomModel
  | ChannelFacebookModel
  | ChannelFormModel
  | ChannelFrontChatModel
  | ChannelGmailModel
  | ChannelGooglePlayModel
  | ChannelIntercomModel
  | ChannelOffice365Model
  | ChannelSmoochModel
  | ChannelSmtpModel
  | ChannelTalkdeskModel
  | ChannelTrulyModel
  | ChannelTwilioModel
  | ChannelTwilioWhatsappModel
  | ChannelTwitterDmModel
  | ChannelTwitterModel
  | ChannelYaloWhatsappModel;
export const RChannel = t.clean<ChannelModel>(RChannelDirty as any);
RChannelIdRef.setTargetType(RChannel);

/*
 * IO methods.
 */

export function parseChannel(src: any): ChannelModel {
  const type = parseType(src.type);

  const baseChannel = {
    id: src.id,
    inboxId: src.inbox_id,
    namespace: src.namespace,
    name: src.display_name,
    displayName: src.display_name,
    address: normalizeHandle(src.contact_source, src.address),
    sendAddress: normalizeHandle(src.contact_source, src.send_as),
    typeName: src.type_name,
    type,
    messageType: parseMessageType(src.message_type),
    contactSource: src.contact_source,
    behaviors: parseChannelBehaviors(src),
    preferences: parseChannelPreferencesBase(src.settings),
    externalChannelTypeId: parseExternalChannelTypeId(src.settings),
    error: (src.error_type && parseChannelError(src)) || undefined,
    updatedAt: src.updated_at,
    url: src.url,
  };

  switch (type) {
    case ChannelTypesEnum.CUSTOM: {
      return {
        ...baseChannel,
        type: ChannelTypesEnum.CUSTOM,
        preferences: parseChannelPreferencesCustom(src.settings, baseChannel.preferences),
        apiUrl: src.api_url,
      };
    }
    case ChannelTypesEnum.FACEBOOK: {
      return {...baseChannel, type: ChannelTypesEnum.FACEBOOK};
    }
    case ChannelTypesEnum.GMAIL: {
      return {
        ...baseChannel,
        type: ChannelTypesEnum.GMAIL,
        preferences: parseChannelPreferencesGmail(src.settings, baseChannel.preferences),
      };
    }
    case ChannelTypesEnum.GOOGLE_PLAY: {
      return {...baseChannel, type: ChannelTypesEnum.GOOGLE_PLAY};
    }
    case ChannelTypesEnum.INTERCOM: {
      return {...baseChannel, type: ChannelTypesEnum.INTERCOM};
    }
    case ChannelTypesEnum.FORM: {
      return {...baseChannel, type: ChannelTypesEnum.FORM};
    }
    case ChannelTypesEnum.OFFICE365: {
      return {
        ...baseChannel,
        type: ChannelTypesEnum.OFFICE365,
        preferences: parseChannelPreferencesOffice365(src.settings, baseChannel.preferences),
      };
    }
    case ChannelTypesEnum.SMOOCH: {
      return {
        ...baseChannel,
        type: ChannelTypesEnum.SMOOCH,
        preferences: parseChannelPreferencesSmooch(src.settings, baseChannel.preferences),
      };
    }
    case ChannelTypesEnum.SMTP: {
      return {
        ...baseChannel,
        type: ChannelTypesEnum.SMTP,
        preferences: parseChannelPreferencesSmtp(src.settings, baseChannel.preferences),
        sendAsDomain: src.send_as_domain,
      };
    }
    case ChannelTypesEnum.TALKDESK: {
      return {...baseChannel, type: ChannelTypesEnum.TALKDESK};
    }
    case ChannelTypesEnum.TRULY: {
      return {...baseChannel, type: ChannelTypesEnum.TRULY};
    }
    case ChannelTypesEnum.TWILIO: {
      return {...baseChannel, type: ChannelTypesEnum.TWILIO};
    }
    case ChannelTypesEnum.TWILIO_WHATSAPP: {
      return {...baseChannel, type: ChannelTypesEnum.TWILIO_WHATSAPP};
    }
    case ChannelTypesEnum.TWITTER: {
      return {
        ...baseChannel,
        type: ChannelTypesEnum.TWITTER,
        preferences: parseChannelPreferencesTwitter(src.settings, baseChannel.preferences),
      };
    }
    case ChannelTypesEnum.TWITTER_DM: {
      return {...baseChannel, type: ChannelTypesEnum.TWITTER_DM};
    }
    case ChannelTypesEnum.YALO_WHATSAPP: {
      return {...baseChannel, type: ChannelTypesEnum.YALO_WHATSAPP};
    }
    case ChannelTypesEnum.FRONT_CHAT: {
      return {
        ...baseChannel,
        type: ChannelTypesEnum.FRONT_CHAT,
        preferences: parseChannelPreferencesFrontChat(src.settings, baseChannel.preferences),
      };
    }
    case ChannelTypesEnum.FRONT_MAIL: {
      return {
        ...baseChannel,
        type: ChannelTypesEnum.FRONT_MAIL,
        preferences: parseChannelPreferencesFrontMail(src.settings, baseChannel.preferences),
      };
    }
    default: {
      throw new Error('Should not happen');
    }
  }
}

export function serializeChannel(src: ChannelModel): any {
  return {
    id: src.id,
    inbox_id: src.inboxId,
    namespace: src.namespace,
    name: src.name,
    address: src.address,
    send_as: src.sendAddress,
    type_name: src.typeName,
    type: parseType(src.type),
    message_type: src.messageType,
    contact_source: src.contactSource,
    behaviors: parseChannelBehaviors(src),
    settings: serializeChannelPreferences(src.preferences, src.type),
    url: src.url,
  };
}

export function parseExternalChannelTypeId(src: any) {
  if (!src.externalChannelTypeId) {
    return undefined;
  }
  return src.externalChannelTypeId;
}

function parseType(type: any): ChannelTypesEnum {
  if (type === 'layer_anon') {
    return ChannelTypesEnum.FRONT_CHAT;
  }

  return type;
}

/*
 * API type.
 */

export const ApiChannel = makeApiSerializableType('channel', RChannel, parseChannel, serializeChannel);
